import axios from 'axios';
import { apiClient } from './client';
import type { ApiResponse, PaginatedResponse } from './client';
import type { PEAnalysis, VMProtectSettings } from './builds';
import type { SelectedFunction } from '../pages/builds/components/FunctionsTab';

// Use relative URL in production (same server), absolute in development
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '/api' : 'http://localhost:8080/api');

export interface StoredFile {
  id: string;
  filename: string;
  size: number;
  hash: string;
  hasPdb: boolean;
  functionsCount?: number;
  buildsCount: number;
  createdAt: string;
  expiresAt: string;
  // Saved config fields
  savedEngine?: string | null;
  savedEngineVersion?: string | null;
  savedSettings?: VMProtectSettings | null;
  savedFunctions?: SelectedFunction[] | null;
  hasConfig: boolean;
}

export interface StoredFileConfig {
  id: string;
  filename: string;
  savedEngine: string | null;
  savedEngineVersion: string | null;
  savedSettings: VMProtectSettings | null;
  savedFunctions: SelectedFunction[] | null;
  hasConfig: boolean;
}

export interface SavedSettings {
  savedEngine?: string | null;
  savedEngineVersion?: string | null;
  savedSettings?: VMProtectSettings | null;
  savedFunctions?: SelectedFunction[] | null;
}

export interface StoredFileDetail extends StoredFile {
  peAnalysis: PEAnalysis;
}

export interface StoredFileUploadResult {
  id: string;
  filename: string;
  size: number;
  hash: string;
  hasPdb: boolean;
  functionsCount?: number;
  peAnalysis: PEAnalysis;
  expiresAt: string;
}

export const storedFilesApi = {
  /**
   * Upload and store a file for reuse (with optional PDB)
   */
  upload: async (file: File, pdbFile?: File): Promise<StoredFileUploadResult> => {
    const formData = new FormData();
    formData.append('file', file);
    if (pdbFile) {
      formData.append('pdb', pdbFile);
    }
    
    const token = localStorage.getItem('accessToken');
    const response = await axios.post<ApiResponse<StoredFileUploadResult>>(
      `${API_URL}/stored-files/upload`,
      formData,
      {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      }
    );
    return response.data.data!;
  },

  /**
   * List user's stored files
   */
  list: async (page = 1, limit = 20): Promise<PaginatedResponse<StoredFile>> => {
    const response = await apiClient.get<PaginatedResponse<StoredFile>>('/stored-files', {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Get stored file details with PE analysis
   */
  get: async (id: string): Promise<StoredFileDetail> => {
    const response = await apiClient.get<ApiResponse<StoredFileDetail>>(`/stored-files/${id}`);
    return response.data.data!;
  },

  /**
   * Get stored file configuration only
   */
  getConfig: async (id: string): Promise<StoredFileConfig> => {
    const response = await apiClient.get<ApiResponse<StoredFileConfig>>(`/stored-files/${id}/config`);
    return response.data.data!;
  },

  /**
   * Delete a stored file
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/stored-files/${id}`);
  },

  /**
   * Extend stored file expiry
   */
  extendExpiry: async (id: string): Promise<{ expiresAt: string }> => {
    const response = await apiClient.post<ApiResponse<{ expiresAt: string }>>(
      `/stored-files/${id}/extend`
    );
    return response.data.data!;
  },

  /**
   * Save/update build settings for a stored file
   */
  saveSettings: async (
    id: string,
    settings: {
      engine?: string;
      engineVersion?: string;
      settings?: VMProtectSettings;
      functions?: SelectedFunction[];
    }
  ): Promise<SavedSettings> => {
    const response = await apiClient.put<ApiResponse<SavedSettings>>(
      `/stored-files/${id}/settings`,
      settings
    );
    return response.data.data!;
  },

  /**
   * Clear saved settings for a stored file
   */
  clearSettings: async (id: string): Promise<void> => {
    await apiClient.delete(`/stored-files/${id}/settings`);
  },
};

